import { camelToKebab, formatFileSize, sleep } from '@/utils'
import axios, { AxiosResponse } from 'axios'
import { toast } from 'vue-sonner'
import { h, Ref } from 'vue'
import configService from '@/services/configService'
import { useUser } from '@/composables/useUser'
import refreshManager from '@/services/refreshServices/refreshManager'
import { AplosFileApiService } from './AplosFileApiService'
import { GetFileUploadUrlArgs } from '../models/GetFileUploadUrlArgs'
import { IAplosFileUploadUrlResponse } from '../models/IAplosFileUploadUrlResponse'
import { GetAplosFileArgs } from '../models/GetAplosFileArgs'
import { AplosFile } from '../models/AplosFile'
import { AplosNcaInputStatus } from '../models/AplosNcaInputStatus'
import { AplosFileCategory } from '../models/AplosFileCategory'
import { InjectableService } from '@/shared/services/InjectableService'


export enum AplosFileUploadFileTypes {

    Csv = "text/csv",
    Xpt = '.xpt', // SAS transport
    Sas7bdat = '.sas7bdat', // SAS dataset
    Xlsx = '.xlsx', // Excel spreadsheet
    Xls = '.xls', // Older Excel spreadsheet
    GenericSpreadSheetFormat = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // Generic spreadsheet format
    Dat = '.dat', // Custom delimiter
    Parquet = '.parquet'
}

class AplosFileUploadStatus {

    public aplosFile?: AplosFile;
    public errors?: any;

}


export class AplosFileUploadService extends InjectableService {

    static readonly injectableKey = 'AplosFileUploadService';

    get injectableKey() {
        return (this.constructor as typeof AplosFileUploadService).injectableKey;
    }

    constructor(private readonly _analysisFileApiService: AplosFileApiService) {
        super();
    }


    public async retryUntilTimeout<TResponse>(apiCall: () => Promise<any>, timeout: number = 30000, interval: number = 5000): Promise<TResponse> {
        if (apiCall == null) throw Error("apiCall is required");
        const endTime = Date.now() + timeout;
        while (Date.now() < endTime) {
            try {
                const response = await apiCall();
                if (response != null) return response;
            } catch (error: any) {
                if (error.response?.status !== 404) throw error;
            }
            await sleep(interval);
        }
        throw new Error('Timeout reached');
    }

    public async handleUpload(
        file: File,
        uploading: Ref<boolean>,
        progress: Ref<number>,
        updateStatus?: (hasError: boolean) => void,
        emit?: (a: 'updateFileRef', ...args: any[]) => void,
    ) {
        const isValidFile = file instanceof File

        if (!isValidFile) {
            toast('the file is not bound to the uploader')
        } else {
            console.debug(`uploading: ${file.name}`)
        }

        const maxFileSize = await configService.getMaxFileSize()
        if (file.size > maxFileSize) {
            toast.error(`File size exceeds the limit of ${formatFileSize(maxFileSize)}: ${file.name}`)
            return
        }

        if (isValidFile) {
            const formData = new FormData()

            uploading.value = true
            let getFileUploadResponse: AxiosResponse<IAplosFileUploadUrlResponse>;

            const auth = useUser()
            await auth.initialize()

            try {
                const uploadArgs = new GetFileUploadUrlArgs({ tenantId: auth.user.tenant_id!, userId: auth.user.id!, fileName: file.name, category: AplosFileCategory.NCAInput })
                getFileUploadResponse = await this._analysisFileApiService.getFileUploadUrl(uploadArgs)
            } catch (error: any) {
                console.error('Error fetching pre-signed URL:', error);
                const errorMessage = error.response?.data?.errors?.[0]?.message || error.message || 'Failed to upload file';
                toast.error(errorMessage);
                uploading.value = false;
                if (updateStatus) {
                    updateStatus(true);
                }
                return;
            }

            try {
                const fields = getFileUploadResponse.data.fields;
                const postPresignedUrl = getFileUploadResponse.data.url

                // NOTE: the order is very important.  s3 api barfs if it's not in the correct order
                for (const key in fields) {
                    formData.append(camelToKebab(key), fields[key]);
                }

                formData.append('file', file);

                await axios.post(postPresignedUrl, formData, {
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                            progress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        }
                    }
                })
                if (updateStatus) {
                    updateStatus(false);
                }

                // start watching for new files at a quicker rate
                const duration = 2 * 60 * 1000; // for x minutes
                const intervals = 5 * 1000; // at x second intervals

                console.log(`refreshing getFiles every ${intervals}ms for ${duration}ms`)
                refreshManager.updateTaskInterval("getFiles", intervals, duration)

                const newFileId = getFileUploadResponse.data.fileId;
                const getFileArgs = new GetAplosFileArgs({ tenantId: auth.user.tenant_id!, userId: auth.user.id!, fileId: newFileId })
                const apiCall = async () => {
                    const getFileLineageResponse = await this._analysisFileApiService.getFileLineage(getFileArgs);
                    return this.tryGetAplosFileForCompletedUpload(newFileId, getFileLineageResponse);
                };

                // Original uploaded if no conversion required or converted
                let uploadStatus = await this.retryUntilTimeout<AplosFileUploadStatus>(apiCall, duration, intervals);
                if (uploadStatus.errors != null) {
                    const errorsObj: Record<string, any> = uploadStatus.errors as Record<string, any>;
                    const errorMessage = Object.keys(errorsObj)
                        .map(key => `<b>${key}</b>: ${errorsObj[key]}`)
                        .join('<br /><br />');
                    throw errorMessage;
                }
                else {
                    if (emit) {
                        emit('updateFileRef', uploadStatus.aplosFile);
                    }
                    toast.success(`Upload success: ${file.name}`);
                }

            } catch (error) {
                toast.error(h('div', { innerHTML: `<b>Upload failed!</b><br /><br /> ${error}` }));
                console.log(`<b>Upload failed:</b><br /> ${error}`);
                if (updateStatus) {
                    updateStatus(true);
                }
            } finally {


                // turn off spinner
                uploading.value = false
                setTimeout(() => {
                    if (!uploading.value) {
                        progress.value = 0;
                    }
                }, 3000); // 3000 milliseconds = 3 seconds

            }
        }

    }

    private tryGetAplosFileForCompletedUpload(inputFileId: string, response: AxiosResponse<Array<AplosFile>>): AplosFileUploadStatus | null {

        const files = response.data;
        let status: AplosFileUploadStatus | null = null;

        if (Array.isArray(files) && files.length) {
            // Try get initial file uploaded, may need conversion
            const originalFile = files.find(x => x.id === inputFileId);
            if (originalFile != null) {
                // If doesn't need conversion upload is considered complete return this ilfe
                const originalIsNcaInput = originalFile?.metadata?.ncaInput === AplosNcaInputStatus.Approved;
                if (originalIsNcaInput && originalFile?.errors == null) {
                    status = new AplosFileUploadStatus();
                    status.aplosFile = originalFile;
                }
                else if (originalFile?.errors == null) {
                    // Else if original required conversion, if not complete wait and retry query, else try get and return converted from lineage
                    const convertedFileId = originalFile?.metadata?.convertedFileId ?? '';
                    const convertedFile = files.find(x => x.id === convertedFileId);
                    const isConversionComplete = convertedFile?.metadata?.ncaInput === AplosNcaInputStatus.Approved;
                    if (convertedFile && isConversionComplete) {
                        status = new AplosFileUploadStatus();
                        status.aplosFile = convertedFile;
                    }
                }
                else if (originalFile?.errors != null) {
                    status = new AplosFileUploadStatus();
                    status.aplosFile = originalFile;
                    status.errors = originalFile?.errors;
                }
            }
        }

        return status;

    }



}